import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type FolioDictation, useDictation } from '../../components/speech-to-text';
import { autoGrow } from './internal-notes-utils';

function preferredLanguageFromI18n(lang: string | undefined): 'fr-FR' | 'en-US' {
  return lang?.startsWith('fr') ? 'fr-FR' : 'en-US';
}

interface NoteDictation {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  dictation: FolioDictation;
  recording: boolean;
  /** Committed value plus the in-flight partial, for display in the textarea. */
  display: string;
}

/**
 * Shared dictation plumbing for the note composer and the note editor:
 * final segments are appended to the current value, the in-flight partial is
 * overlaid for display, and while recording Enter commits / Esc discards.
 */
export function useNoteDictation({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange: (next: string) => void;
}): NoteDictation {
  const { i18n } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  // Esc during dictation must drop the partial. We can't expose a cancel() from
  // useDictation without changing folio's SPEECH_TO_TEXT API, so we intercept onFinalSegment
  // with this flag. The flag stays sticky until the next recording starts —
  // transcribe loop may emit a few late final segments after stop().
  const cancellingRef = useRef(false);

  const handleFinal = useCallback(
    (segment: string) => {
      if (cancellingRef.current) return;
      const cur = valueRef.current;
      onValueChange(cur ? `${cur} ${segment}`.trim() : segment);
    },
    [onValueChange],
  );

  const dictation = useDictation({
    onFinalSegment: handleFinal,
    preferredLanguage: preferredLanguageFromI18n(i18n.language),
  });
  const recording = dictation.state === 'recording';
  const display = dictation.partial ? `${value}${value ? ' ' : ''}${dictation.partial}` : value;

  useEffect(() => {
    if (recording) cancellingRef.current = false;
  }, [recording]);

  useEffect(() => {
    autoGrow(textareaRef.current);
  }, [display]);

  // Global keys during dictation: Enter commits the partial, Esc discards it.
  // Document-level (capture phase) so we win over the textarea's own onKeyDown
  // (Esc would otherwise close the editor) and the mic-button focus.
  useEffect(() => {
    if (!recording) return;
    function onKey(event: globalThis.KeyboardEvent) {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        dictation.stop();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancellingRef.current = true;
        dictation.stop();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [recording, dictation]);

  return { textareaRef, dictation, recording, display };
}
